/**
 * Dates of a timeline as people read them (« 2 oct. », « 14:30 » when the
 * event has a time), the month they belong to, and whether they are still
 * ahead (planned). Invalid dates are shown as given and never count as planned.
 */
export interface TimelineDate {
  label: string;
  time: string | null;
  month: string;
  upcoming: boolean;
}

export const timelineDate = (value: string, now: Date = new Date()): TimelineDate => {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return { label: value, time: null, month: '', upcoming: false };
  const sameYear = date.getFullYear() === now.getFullYear();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
  return {
    label: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) }),
    time: dateOnly ? null : date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    month: date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    upcoming: dateOnly ? date.getTime() >= endOfToday : date.getTime() > now.getTime(),
  };
};

/** Events in the given order, cut into runs of the same month (headers only when the timeline spans several). */
export const groupByMonth = <T extends { date: string }>(events: T[], now: Date = new Date()): Array<{ month: string; events: T[] }> => {
  const groups: Array<{ month: string; events: T[] }> = [];
  for (const event of events) {
    const month = timelineDate(event.date, now).month;
    const last = groups.at(-1);
    if (last && last.month === month) last.events.push(event);
    else groups.push({ month, events: [event] });
  }
  return groups;
};
